import React, { forwardRef, useImperativeHandle, useRef, useState, useEffect, useLayoutEffect, useCallback } from "react";
import { API_BASE } from "../api";
import "./PreviewPane.css";

// The three Videri device form factors. We preview at native resolution (not
// "fill the pane") and use CSS transform: scale() to shrink-to-fit, so what
// you see here is exactly what the device sees — including any clipping.
const ASPECT_RATIOS = [
  { id: "portrait",  label: "Portrait",  icon: "📱", width: 1080, height: 1920 },
  { id: "landscape", label: "Landscape", icon: "🖥",  width: 1920, height: 1080 },
  { id: "square",    label: "Square",    icon: "⬛", width: 1920, height: 1920 },
];

const STORAGE_KEY = "videri-forge-preview-aspect";

function loadPreferredAspect() {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (v && ASPECT_RATIOS.some((a) => a.id === v)) return v;
  } catch { /* noop */ }
  return "portrait";
}

const PreviewPane = forwardRef(function PreviewPane({ url, appName }, ref) {
  const iframeRef = useRef(null);
  const stageRef = useRef(null);       // the visible area we scale to fit
  const frameWrapRef = useRef(null);   // the native-sized wrapper we transform
  const [key, setKey] = useState(0);
  const [aspectId, setAspectId] = useState(loadPreferredAspect);
  const [scale, setScale] = useState(1);
  const [clipped, setClipped] = useState(false);
  const [contentHeight, setContentHeight] = useState(null);

  const aspect = ASPECT_RATIOS.find((a) => a.id === aspectId) || ASPECT_RATIOS[0];
  const effectiveHeight = contentHeight !== null ? Math.max(aspect.height, contentHeight) : aspect.height;

  useImperativeHandle(ref, () => ({
    reload() {
      setKey((k) => k + 1);
      setClipped(false);
      setContentHeight(null);
    },
  }));

  // Persist aspect selection so users don't have to re-pick every session.
  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, aspectId); } catch { /* noop */ }
  }, [aspectId]);

  // Fit-to-pane: compute the scale factor that makes the native-sized iframe
  // fully visible inside the stage, preserving aspect ratio. Recomputes on
  // viewport resize and when the user switches orientation.
  const recomputeScale = useCallback(() => {
    const stage = stageRef.current;
    if (!stage) return;
    const { clientWidth, clientHeight } = stage;
    if (clientWidth < 20 || clientHeight < 20) return;
    // 16px padding on each side so the device frame doesn't kiss the edges
    const availW = Math.max(40, clientWidth - 32);
    const availH = Math.max(40, clientHeight - 32);
    const s = Math.min(availW / aspect.width, availH / aspect.height);
    setScale(s);
  }, [aspect.width, aspect.height]);

  // Reset measured content height when the iframe remounts (reload or aspect change)
  useEffect(() => { setContentHeight(null); }, [key, aspectId]);

  useLayoutEffect(() => {
    recomputeScale();
  }, [recomputeScale, aspectId]);

  useEffect(() => {
    if (!stageRef.current) return;
    const ro = new ResizeObserver(() => recomputeScale());
    ro.observe(stageRef.current);
    return () => ro.disconnect();
  }, [recomputeScale]);

  // Clipping detection: on load (and on each reload) we wait for the iframe's
  // content to settle, then measure scrollWidth/scrollHeight vs. the device
  // dimensions. If content overflows the visible area in the target
  // orientation, the toolbar flips to a red warning so users catch the bug
  // without deploying to device first.
  const checkClipping = useCallback(() => {
    const frame = iframeRef.current;
    if (!frame) return;
    try {
      // Same-origin check — we serve the preview from our own proxy, so this
      // works. If the iframe ever becomes cross-origin, the try/catch keeps
      // us from blowing up.
      const doc = frame.contentDocument;
      if (!doc || !doc.body) return;
      const scrollW = Math.max(doc.documentElement.scrollWidth, doc.body.scrollWidth);
      const scrollH = Math.max(doc.documentElement.scrollHeight, doc.body.scrollHeight);
      // A couple of px tolerance — browser sub-pixel rounding causes false
      // positives if we check strict > equality.
      const overflows = scrollW > aspect.width + 4 || scrollH > aspect.height + 4;
      setClipped(overflows);
      if (scrollH > aspect.height + 4) setContentHeight(scrollH);
    } catch {
      setClipped(false);
    }
  }, [aspect.width, aspect.height]);

  const onFrameLoad = () => {
    // Give layout/fonts a beat to settle, then measure
    setTimeout(checkClipping, 500);
    setTimeout(checkClipping, 1500);
  };

  // Re-measure on orientation change in case content was fine in portrait
  // but would overflow in landscape.
  useEffect(() => { setTimeout(checkClipping, 100); }, [aspectId, checkClipping]);

  return (
    <div className="preview-pane">
      {url ? (
        <>
          <div className="preview-toolbar">
            <span className="preview-url">{url}</span>
            <div className="preview-toolbar-actions">
              <div className="aspect-selector" role="group" aria-label="Preview aspect ratio">
                {ASPECT_RATIOS.map((a) => (
                  <button
                    key={a.id}
                    className={`aspect-btn ${aspectId === a.id ? "active" : ""}`}
                    onClick={() => setAspectId(a.id)}
                    title={`${a.label} ${a.width}×${a.height}`}
                  >
                    <span className="aspect-icon">{a.icon}</span>
                    <span className="aspect-label">{a.label}</span>
                  </button>
                ))}
              </div>
              {clipped && (
                <span className="clipping-warning" title="Content is being cut off in this orientation. Ask the AI to fix the layout for this aspect ratio.">
                  ⚠ Content clipped
                </span>
              )}
              <span className="scale-badge" title={`Native ${aspect.width}×${aspect.height}, scaled to fit`}>
                {aspect.width}×{aspect.height} · {Math.round(scale * 100)}%
              </span>
              {appName && (
                <a
                  className="preview-docs-link"
                  href={`${API_BASE}/docs/${appName}/html`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Docs
                </a>
              )}
              <button className="preview-reload" onClick={() => { setKey((k) => k + 1); setClipped(false); }} title="Reload preview">
                &#8635;
              </button>
            </div>
          </div>
          <div className="preview-stage" ref={stageRef}>
            {/*
              Two-layer scale container:
              - Outer wrapper has the VISIBLE dimensions (native × scale), so
                flexbox centering treats it as its rendered size.
              - Inner wrapper has the NATIVE device dimensions (1080×1920,
                1920×1080, 1920×1920) and is transform-scaled from top-left
                so its pre-scale origin is flush with the outer's top-left.
              This replaces the earlier "scale a natively-sized element from
              center" approach, which had a well-known bug: CSS transforms
              don't affect box-model layout, so a 1920-wide scaled element
              still took 1920px in flex layout → got clipped/shifted
              unpredictably in landscape and square.
            */}
            <div
              className="preview-scale-outer"
              style={{
                width: Math.max(1, Math.round(aspect.width * scale)),
                height: Math.max(1, Math.round(effectiveHeight * scale)),
              }}
            >
              <div
                className="preview-frame-wrap"
                ref={frameWrapRef}
                style={{
                  width: aspect.width,
                  height: effectiveHeight,
                  transform: `scale(${scale})`,
                  transformOrigin: "top left",
                }}
              >
                <iframe
                  ref={iframeRef}
                  key={`${key}-${aspectId}`}
                  className="preview-iframe"
                  src={`${url}${url.includes("?") ? "&" : "?"}_cb=${key}&orient=${aspectId}`}
                  title="Integration Preview"
                  onLoad={onFrameLoad}
                  width={aspect.width}
                  height={effectiveHeight}
                />
              </div>
            </div>
          </div>
        </>
      ) : (
        <div className="preview-empty">
          <div className="preview-empty-icon">&#9881;</div>
          <h3>Live Preview</h3>
          <p>
            Your integration will appear here after you deploy it. Start by
            describing what you want to display in the chat.
          </p>
        </div>
      )}
    </div>
  );
});

export default PreviewPane;
